import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useSelector, useDispatch } from "react-redux";
import useHandleBlur from "../../../functions/useHandleBlur";
// import { useFetchJobDetails } from "../../../functions/useFetchJobDetails"; // Reusable API hook
import { fetchJobDetails } from "../../../redux/slices/fetchJobDetailsSlice"; // Import for fetchJobDetails
import { useFormState } from "../../../functions/useFormState"; // Reusable form hook
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import {
  selectSequenceOfOperations,
  selectPumpDetails,
  selectFieldDescriptions,
} from "../../../redux/selectors";
import { selectGlobalVariables } from "../../../redux/slices/globalVariablesSlice"; // Import the selector
import { selectApiStatus } from "../../../redux/slices/apiStatusSlice"; // Import the selector
import useFieldClass from "../../../functions/useFieldClass"; // Import the useFieldClass hook
import NoRecordFoundAlert from "../../common/NoRecordFoundAlert"; // Import NoRecordFoundAlert component


const SequenceOfOperations = (zone) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails); // User details
  const userRoles = useSelector(selectUserRoles); // User details
  const currentUser = userDetails?.name;
  const sectionName = "sequenceOfOperations"; // Name of the component

  const apiUrls = useSelector(selectApiUrls); // Fetch API URLs from Redux
  const apiUrl = apiUrls[sectionName];
  const jockeyUrl = apiUrls["pumpDetails"];
  const tableKey = sectionName;

  const globalVariables = useSelector(selectGlobalVariables); // Fetch global variables

  const jockeyReference = useMemo(() => {
    const jockeyUrlVariable = globalVariables.find(
      (variable) => variable.Title === "Jockey URL"
    );
    return jockeyUrlVariable?.VariableValue || "";
  }, [globalVariables]);

  const { options } = useSelector((state) => state.dropdown); // Fetch dropdown values from redux

  const jockeySequenceOptions =
  (options?.filter((option) => option.DropdownType === "Jockey Sequence") || []).sort(
    (a, b) => a.SortOrder - b.SortOrder
  );
  
  const storedJobDetails = useSelector(selectSequenceOfOperations);

  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );

  const JockeyDetails = useSelector(selectPumpDetails);
  const jockeySequence = useMemo(() => {
    if (JockeyDetails.length === 1) {
      return JockeyDetails[0];
    }
  
    // First try to find a pump with PumpID of "Jockey" in the current zone
    const jockeyPump = JockeyDetails.find(
      (detail) => detail.PumpID && detail.PumpID.toLowerCase() === "jockey" && detail.Zone === activetab
    );
  
    if (jockeyPump) {
      return jockeyPump;
    }
  
    // If not found, look for any pump with JockeySequence defined in the current zone
    const withJockeySequence = JockeyDetails.find(
      (detail) => detail.JockeySequence && detail.Zone === activetab
    );
  
    if (withJockeySequence) {
      return withJockeySequence;
    }
  
    // If not found, look for a pump with PumpID of "Main" in the current zone
    const mainPump = JockeyDetails.find(
      (detail) => detail.PumpID && detail.PumpID.toLowerCase() === "main" && detail.Zone === activetab
    );
  
    if (mainPump) {
      return mainPump;
    }
  
    // If not found, use any other available pump in the current zone
    const zoneFiltered = JockeyDetails.filter(detail => detail.Zone === activetab);
    if (zoneFiltered.length > 0) {
      return zoneFiltered[0];
    }
    
    // If no pumps in current zone, return the first pump or empty object
    return JockeyDetails[0] || {};
  }, [JockeyDetails, activetab]);
  
    const apiStatus = useSelector(selectApiStatus); // Fetch API status from Redux
    const loading = apiStatus[sectionName] !== "success"; // Determine loading state based on API status

  const error = null;

  const jobDetails = useMemo(() => {
    const detail = storedJobDetails.find((detail) => detail.Zone === activetab);
    return detail || storedJobDetails[0] || {};
  }, [storedJobDetails, activetab]);

  const { formData, handleChange, setFormData } = useFormState({});
  useEffect(() => {
    if (jobDetails) {
      setFormData({
        ID: jobDetails.ID || "",
        alarmOne: jobDetails.AlarmOne || "",
        alarmTwo: jobDetails.AlarmTwo || "",
        alarmThree: jobDetails.AlarmThree || "",
        alarmFour: jobDetails.AlarmFour || "",
        type: jobDetails.Type || "",
        jockeySequence: jockeySequence.JockeySequence || "",
        comments: jobDetails.Comments || "",
      });
    }
  }, [dispatch, jobDetails, setFormData, jockeySequence.JockeySequence]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
    const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

  // State to store role variables
  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
    Engineer: false,
    TestPit: false,
    PSN: false,
    Sales: false,
    ReadOnly: false,
  });

  const { handleFocus, handleBlur, showNotification, saveStatus } =
    useHandleBlur(formData);

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  const fieldClasses = useFieldClass(formData, zone); // Use useFieldClass hook

  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ); // Show loading spinner
  }

  if (error) {
    return <div className="error">{error}</div>; // Show error message if fetch fails
  }

  if (!loading && !jobDetails?.ID) {
    return (
      <NoRecordFoundAlert sectionName={sectionName} zone={zone} />
    );
  }

  /****************Add dropdown options here*************************/

  /***************************************************************** */

  return (
    <div>
      {showNotification && (
        <div
          className={`save-notification ${
            saveStatus.includes("Failed") ? "error" : ""
          }`}
        >
          {saveStatus}
        </div>
      )}
      <div className="mechanical-responsive-grid sequence-operations-responsive">
      <p className="sequence-operations-note">ALARMS: Low pressure, high pressure, phase/voltage and VFD fault (when applicable) are standard alarms. Please list additional alarms below:</p>
        
      <Row className="mb-3">
        <Col>
          <Form.Group as={Row} controlId="type">
            <Form.Label column md={3}>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip("Type")}
                delay={{ show: 300, hide: 200 }}
              >
                <span>Type</span>
              </OverlayTrigger>
            </Form.Label>
            <Col>
              <Form.Control
                type="text"
                name="type"
                value={formData.type || ""}
                onFocus={handleFocus}
                onBlur={(e) =>
                  handleBlur(e, "Type", [], {
                    ID: formData.ID,
                    url: apiUrl,
                    field: "Type",
                    tableKey,
                  })
                }
                disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                // Apply dynamic class
                        onChange={handleChange}
                        placeholder="Enter Type"
                        />
                      </Col>
                      </Form.Group>
                    </Col>

                    <Col>
                      <Form.Group as={Row} controlId="jockeySequence">
                      <Form.Label column md={3}>
                        <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("Jockey Sequence")}
                        delay={{ show: 300, hide: 200 }}
                        >
                        <a href={jockeyReference} target="_blank" rel="noopener noreferrer">
                          Jockey Sequence
                        </a>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col>
                        <Form.Select
                        name="jockeySequence"
                        value={formData.jockeySequence || ""}
                        onFocus={handleFocus}
                        onBlur={(e) => {
                  handleBlur(e, "Jockey Sequence", jockeySequenceOptions, {
                    ID: jockeySequence.ID,
                    url: jockeyUrl,
                    field: "JockeySequence",
                    tableKey: "pumpDetails",  // Use pumpDetails table key instead of sequenceOfOperations
                  });
                  // Force refresh PumpDetails data after update
                  setTimeout(() => {
                    dispatch(fetchJobDetails({
                      jobId: activetab,
                      apiUrls: { pumpDetails: jockeyUrl }
                    }));
                  }, 500);
                }}
                disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                className={fieldClasses.jockeySequence} // Apply dynamic class
                onChange={handleChange}
              >
                <option value="">Select Jockey Sequence</option>
                {jockeySequenceOptions.map((option) => (
                  <option key={option.ID} value={option.DisplayText}>
                    {option.DisplayText}
                  </option>
                ))}
                {!jockeySequenceOptions.some(
                  (option) => option.DisplayText === formData.jockeySequence
                ) &&
                  formData.jockeySequence && (
                    <option value={formData.jockeySequence} style={{ color: "red" }}>
                      *{formData.jockeySequence}
                    </option>
                  )}
              </Form.Select>
            </Col>
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3 mechanical-form-full-width">
        <Col>
          <Form.Group controlId="comments">
            <Form.Label>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip("Comments")}
                delay={{ show: 300, hide: 200 }}
              >
                <span>Comments</span>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              name="comments"
              value={formData.comments || ""}
              onFocus={handleFocus}
              onBlur={(e) =>
                handleBlur(e, "Comments", [], {
                  ID: formData.ID,
                  url: apiUrl,
                  field: "Comments",
                  tableKey,
                })
              }
              disabled={isDisabled || (!roles.Admin && !roles.Apps)}
              className={fieldClasses.comments} // Apply dynamic class
              onChange={handleChange}
              placeholder="Enter Comments"
            />
          </Form.Group>
        </Col>
      </Row>
      </div>
    </div>
  );
};

export default SequenceOfOperations;
